/**
 * Feature-scoped credential uploads — the Apple Wallet signing certificate and
 * pass images (WALLET-UPLOAD-BRIEF.md, 2026-08-06).
 *
 * Constraints are READ FROM THE FIELD's `upload` block ({accept, maxBytes,
 * exactWidth/exactHeight or maxWidth/maxHeight}) — never hardcoded — so this
 * form and the validator behind PUT …/credentials/:fieldKey agree by
 * construction. A certificate and an image render differently on purpose: an
 * image has no expiry, and only a certificate yields derived identifiers.
 *
 * The response carries ONLY derived, non-secret facts (configured, byteLength,
 * expiresAt, passTypeIdentifier/teamIdentifier). File bytes and the password
 * are never displayed, logged, or echoed.
 */
import { useMemo, useRef, useState } from "react";
import { Btn, Icon, Pill, T } from "@v0y4ge/jrni-ui";
import { ApiError, putIntegrationFeatureCredential } from "@v0y4ge/api-client";
import { toast } from "../../shell/toast";
import { walletAssetStore } from "./WalletPassPreview";
import { useT } from "../../lib/i18n";

type Bag = Record<string, unknown>;
interface UploadSpec { accept: string[]; maxBytes: number; exactWidth?: number; exactHeight?: number; maxWidth?: number; maxHeight?: number }
interface CredState { configured: boolean; byteLength: number | null; expiresAt: string | null; derived: Record<string, string> | null; fileName?: string }

const uploadOf = (f: Bag): UploadSpec | null => {
  const u = f.upload as Bag | undefined;
  if (!u || typeof u.maxBytes !== "number") return null;
  return {
    accept: Array.isArray(u.accept) ? u.accept.map(String) : [],
    maxBytes: u.maxBytes,
    exactWidth: typeof u.exactWidth === "number" ? u.exactWidth : undefined,
    exactHeight: typeof u.exactHeight === "number" ? u.exactHeight : undefined,
    maxWidth: typeof u.maxWidth === "number" ? u.maxWidth : undefined,
    maxHeight: typeof u.maxHeight === "number" ? u.maxHeight : undefined,
  };
};
const isCert = (f: Bag) => String(f.type ?? "") === "signing-certificate";
const kb = (n: number) => (n >= 1024 ? `${Math.round(n / 1024)} KiB` : `${n} B`);
const extFor = (accept: string[]) => accept.includes("application/x-pkcs12") ? ".p12" : accept.map((a) => "." + (a.split("/")[1] || "file")).join(", ");

/** base64 without a data: prefix, chunked so large-ish files don't blow the stack. */
async function fileToBase64(file: File): Promise<string> {
  const buf = new Uint8Array(await file.arrayBuffer());
  let bin = "";
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode.apply(null, Array.from(buf.subarray(i, i + 0x8000)));
  return btoa(bin);
}
function pngDimensions(file: File): Promise<{ w: number; h: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve({ w: img.naturalWidth, h: img.naturalHeight }); };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

/** One field's uploader card. `passwordDraft` rides the certificate PUT only. */
function CredentialUploadField({ provider, featureKey, field, canWrite, password, onConfigured }: {
  provider: string; featureKey: string; field: Bag; canWrite: boolean; password?: string; onConfigured?: () => void;
}) {
  const t = useT();
  const spec = uploadOf(field)!;
  const cert = isCert(field);
  const fieldKey = String(field.key ?? "");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [cred, setCred] = useState<CredState>(() => {
    const c = field.credential as Bag | undefined;
    return c && typeof c === "object"
      ? { configured: true, byteLength: typeof c.byteLength === "number" ? c.byteLength : null,
          expiresAt: typeof c.expiresAt === "string" ? c.expiresAt : null,
          derived: c.derived && typeof c.derived === "object" ? c.derived as Record<string, string> : null }
      : { configured: !!field.configured, byteLength: null, expiresAt: null, derived: null };
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const constraint = useMemo(() => {
    const parts = [extFor(spec.accept), t("settings.walletUpload.upTo", "up to") + " " + kb(spec.maxBytes)];
    if (spec.exactWidth && spec.exactHeight) parts.push(t("settings.walletUpload.exactly", "exactly") + ` ${spec.exactWidth}\u00d7${spec.exactHeight}`);
    else if (spec.maxWidth && spec.maxHeight) parts.push(t("settings.walletUpload.upTo", "up to") + ` ${spec.maxWidth}\u00d7${spec.maxHeight}`);
    return parts.join(" \u00b7 ");
  }, [spec, t]);

  const fail = (msg: string) => { setError(msg); setBusy(false); };
  const onFile = async (file: File) => {
    setError(null);
    // Local pre-checks quote the SAME limits the server enforces — fast feedback,
    // one vocabulary. The mock cannot check PNG dimensions; the browser can.
    const typeOk = spec.accept.length === 0 || spec.accept.includes(file.type)
      || (spec.accept.includes("application/x-pkcs12") && /\.p12$/i.test(file.name));
    if (!typeOk) return fail(cert ? t("settings.walletUpload.expectedP12", "Expected a .p12.") : t("settings.walletUpload.expectedPng", "Expected a PNG."));
    if (file.size > spec.maxBytes) return fail(t("settings.walletUpload.overLimit", "Too large — the limit is") + " " + kb(spec.maxBytes) + ".");
    if (!cert && (spec.exactWidth || spec.maxWidth)) {
      const dim = await pngDimensions(file);
      if (!dim) return fail(t("settings.walletUpload.unreadableFile", "That file could not be read."));
      if (spec.exactWidth && spec.exactHeight && (dim.w !== spec.exactWidth || dim.h !== spec.exactHeight))
        return fail(t("settings.walletUpload.mustBeExactly", "Must be exactly") + ` ${spec.exactWidth}\u00d7${spec.exactHeight} \u2014 ${t("settings.walletUpload.thisFileIs", "this file is")} ${dim.w}\u00d7${dim.h}.`);
      if (spec.maxWidth && spec.maxHeight && (dim.w > spec.maxWidth || dim.h > spec.maxHeight))
        return fail(t("settings.walletUpload.mustFitWithin", "Must fit within") + ` ${spec.maxWidth}\u00d7${spec.maxHeight} \u2014 ${t("settings.walletUpload.thisFileIs", "this file is")} ${dim.w}\u00d7${dim.h}.`);
    }
    setBusy(true);
    try {
      const value = await fileToBase64(file);
      // Feed the front-preview card: session-only object URL for the file just
      // chosen. The PUT response returns derived facts only, so this is the one
      // moment the browser can honestly show the asset.
      walletAssetStore.put(provider, featureKey, fieldKey, file);
      const body: { value: string; contentType: string; password?: string } = {
        value, contentType: file.type || spec.accept[0] || "application/octet-stream",
      };
      if (cert && password) body.password = password;
      const res = await putIntegrationFeatureCredential(provider, featureKey, fieldKey, body) as Bag;
      const d = (res && typeof res === "object" && "data" in res ? (res as { data: Bag }).data : res) as Bag;
      setCred({
        configured: d?.configured !== false,
        byteLength: typeof d?.byteLength === "number" ? d.byteLength : file.size,
        expiresAt: typeof d?.expiresAt === "string" ? d.expiresAt : null,
        derived: d?.derived && typeof d.derived === "object" ? d.derived as Record<string, string> : null,
        fileName: file.name,
      });
      toast({ tone: "success", title: String(field.label ?? fieldKey) + " " + t("settings.walletUpload.uploaded", "uploaded") });
      if (onConfigured) onConfigured();
    } catch (e) {
      const code = e instanceof ApiError ? e.code : "";
      setError(
        code === "INVALID_BODY" ? t("settings.walletUpload.unreadableFile", "That file could not be read.")
          : code === "UNSUPPORTED_MEDIA_TYPE" ? (cert ? t("settings.walletUpload.expectedP12", "Expected a .p12.") : t("settings.walletUpload.expectedPng", "Expected a PNG."))
            : code === "INVALID_CREDENTIAL_FORMAT" ? t("settings.walletUpload.rejectedFormat", "Rejected — check the size and dimensions:") + " " + constraint
              : code === "FIELD_NOT_UPLOADABLE" ? t("settings.walletUpload.notUploadable", "This field takes typed input, not a file.")
                : e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  const expiry = cred.expiresAt ? new Date(cred.expiresAt) : null;
  const expiryLabel = expiry && !isNaN(expiry.getTime()) ? expiry.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : cred.expiresAt;
  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: T.n1 }}>
          {String(field.label ?? fieldKey)}{field.required ? <span style={{ color: T.red1 }}> *</span> : null}
        </span>
        {cred.configured
          ? <Pill tone="success" dot>{t("settings.walletUpload.configured", "Configured")}</Pill>
          : <Pill tone="neutral">{t("settings.walletUpload.notConfigured", "Not configured")}</Pill>}
        <span style={{ flex: 1 }} />
        <Btn variant="secondary" size="sm" icon={<Icon.upload size={13} />} disabled={!canWrite || busy}
          onClick={() => inputRef.current?.click()}>
          {busy ? t("settings.walletUpload.uploading", "Uploading\u2026")
            : cred.configured ? t("settings.walletUpload.replace", "Replace") : t("settings.walletUpload.upload", "Upload")}
        </Btn>
        <input ref={inputRef} type="file" accept={spec.accept.concat(cert ? [".p12"] : []).join(",")} style={{ display: "none" }}
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) void onFile(f); }} />
      </div>
      <div style={{ fontSize: 11.5, color: T.soft }}>{constraint}</div>
      {typeof field.description === "string" && field.description ? (
        <div style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.5 }}>{field.description}</div>
      ) : null}
      {cred.configured && cred.byteLength != null ? (
        <div style={{ fontSize: 12, color: T.n2, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          {cred.fileName ? <span>{cred.fileName}</span> : null}
          <span>{kb(cred.byteLength)}</span>
          {/* Certificates expire; images don't. Never say "expires" about a PNG. */}
          {cert
            ? <span style={{ color: T.n2 }}>{cred.expiresAt ? t("settings.walletUpload.expires", "Expires") + " " + expiryLabel : t("settings.walletUpload.expiryPending", "Expiry read on the server")}</span>
            : null}
        </div>
      ) : null}
      {cert && cred.derived ? (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {cred.derived.passTypeIdentifier ? <code style={{ fontFamily: T.fontMono, fontSize: 11, background: T.n6, padding: "3px 8px", borderRadius: 4, color: T.p1 }}>{cred.derived.passTypeIdentifier}</code> : null}
          {cred.derived.teamIdentifier ? <code style={{ fontFamily: T.fontMono, fontSize: 11, background: T.n6, padding: "3px 8px", borderRadius: 4, color: T.n2 }}>{t("settings.walletUpload.team", "Team")} {cred.derived.teamIdentifier}</code> : null}
        </div>
      ) : null}
      {error ? <div style={{ fontSize: 12, color: T.red1, lineHeight: 1.5 }}>{error}</div> : null}
    </div>
  );
}

/**
 * The whole upload group for a feature whose configFields carry `upload`
 * blocks. The certificate password is a text input that rides the certificate
 * PUT (`password` param) — it is not an upload, and it is optional on purpose.
 */
export function FeatureCredentialUploads({ provider, featureKey, fields, canWrite, onSaved }: {
  provider: string; featureKey: string; fields: Bag[]; canWrite: boolean;
  /* Fired after a credential PUT resolves so the parent can re-read the
   * registration: with EFFECTIVE enable-state, uploading the last required
   * credential is what flips requires_input → Active. */
  onSaved?: () => void;
}) {
  const t = useT();
  const uploads = fields.filter((f) => uploadOf(f));
  const pwField = fields.find((f) => !uploadOf(f) && String(f.type ?? "") === "password" && /password/i.test(String(f.key ?? "")));
  const [pw, setPw] = useState("");
  const [pwTouched, setPwTouched] = useState(false);
  const [done, setDone] = useState<Record<string, boolean>>(
    () => Object.fromEntries(uploads.map((f) => [String(f.key), !!f.configured || !!f.credential])),
  );
  // Child cards own their upload state; the pair banner reads the initial
  // `configured` flags plus this session's uploads via onConfigured.
  const required = uploads.filter((f) => !!f.required);
  const missing = required.filter((f) => !done[String(f.key)]);
  return (
    <div style={{ display: "grid", gap: 10 }}>
      {/* Required-pair honesty: a certificate alone is NOT "done" — Apple
        * refuses a pass without the 29×29 icon. */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 11px", borderRadius: 6, background: missing.length ? "#FFF6E5" : "#EAF7F0", border: `1px solid ${missing.length ? "#F5DCA8" : "#BFE8D2"}` }}>
        <span style={{ color: missing.length ? "#B54708" : "#027D47", display: "inline-flex", marginTop: 1 }}><Icon.info size={12} /></span>
        <span style={{ fontSize: 12, color: missing.length ? "#8A6A05" : "#0F7A3D", lineHeight: 1.5 }}>
          {missing.length
            ? t("settings.walletUpload.pairIncomplete", "Passes stay off until every required file is up:") + " " + missing.map((f) => String(f.label ?? f.key)).join(" \u00b7 ")
            : t("settings.walletUpload.pairComplete", "All required files are configured \u2014 passes can be signed.")}
        </span>
      </div>
      {uploads.map((f) => {
        const k = String(f.key);
        return (
          <div key={k}>
            <CredentialUploadField provider={provider} featureKey={featureKey} field={f} canWrite={canWrite}
              password={isCert(f) ? pw : undefined} onConfigured={() => { setDone((d) => ({ ...d, [k]: true })); onSaved?.(); }} />
            {isCert(f) && pwField ? (
              <div style={{ margin: "8px 0 0", display: "grid", gap: 4 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: T.n2 }}>{String(pwField.label ?? t("settings.walletUpload.certificatePassword", "Certificate password"))}</label>
                <input type="password" value={pw} autoComplete="new-password" disabled={!canWrite}
                  onChange={(e) => { setPw(e.target.value); setPwTouched(true); }}
                  placeholder={t("settings.walletUpload.pwPlaceholder", "Sent with the certificate upload \u2014 may be empty")}
                  style={{ height: 34, border: `1px solid ${T.n4}`, borderRadius: 6, padding: "0 10px", fontSize: 13, fontFamily: "inherit", background: canWrite ? T.white : T.n6 }} />
                <span style={{ fontSize: 11, color: T.soft }}>
                  {typeof pwField.description === "string" && pwField.description
                    ? pwField.description
                    : t("settings.walletUpload.pwHint", "Optional \u2014 an empty password is legitimate for Keychain exports.")}
                  {pwTouched && !pw ? " " + t("settings.walletUpload.pwCleared", "(will be sent empty)") : ""}
                </span>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

